import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api, type User } from '@/lib/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  /**
   * Bei Bestaetigungspflicht legt der Server das Konto ohne Sitzung an -
   * der Aufrufer muss dann "Postfach pruefen" zeigen statt zu navigieren.
   */
  register: (email: string, password: string, name?: string) => Promise<{ verificationRequired: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: current } = await api.auth.me();
      setUser(current);
    } catch (error) {
      // 401 ist der Normalfall fuer nicht angemeldete Besucher
      if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isAdmin: user?.role === 'ADMIN',
      refresh,
      login: async (email, password, totpCode) => {
        const { user: current } = await api.auth.login(email, password, totpCode);
        setUser(current);
      },
      register: async (email, password, name) => {
        const result = await api.auth.register(email, password, name);
        // Ohne Sitzung keinen Benutzer setzen - sonst laufen alle folgenden
        // Abrufe in 401 und die Oberflaeche meldet "Nicht authentifiziert".
        if (!result.verificationRequired) setUser(result.user);
        return { verificationRequired: result.verificationRequired };
      },
      logout: async () => {
        await api.auth.logout();
        setUser(null);
      },
    }),
    [user, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return context;
}
